import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import {
  Globe,
  Play,
  Square,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Terminal,
  Database,
  Package,
  TrendingDown,
  ExternalLink,
  Image,
  Images,
  UploadCloud,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';

const SCRAPER_SERVICE_URL = 'http://localhost:7078';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface CategoryInfo {
  index: number;
  name: string;
  url: string;
}

interface ScraperStateFromApi {
  running: boolean;
  stopped: boolean;
  started_at: string | null;
  finished_at: string | null;
  categories_scraped: number;
  total_products_found: number;
  total_inserted: number;
  total_updated: number;
  current_category: string | null;
  errors: string[];
  logs: Array<{ timestamp: string; level: string; message: string }>;
}

interface ServiceStatus {
  state: ScraperStateFromApi;
  categories: CategoryInfo[];
  today_record_count: number | null;
  supabase_configured: boolean;
}

interface ProductRecord {
  id: string;
  product_code: string | null;
  sku: string | null;
  product_name: string;
  brand: string | null;
  category: string | null;
  sub_category: string | null;
  price: number | null;
  discounted_price: number | null;
  currency: string;
  image_url: string | null;
  product_url: string | null;
  scrape_date: string;
  scraped_at: string;
}

interface ImageScraperStateFromApi {
  running: boolean;
  stopped: boolean;
  started_at: string | null;
  finished_at: string | null;
  total_products: number;
  processed: number;
  products_with_images: number;
  total_images_mirrored: number;
  current_product: string | null;
  errors: string[];
  logs: Array<{ timestamp: string; level: string; message: string }>;
}

interface ImageServiceStatus {
  state: ImageScraperStateFromApi;
  supabase_configured: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTurkishPrice(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
}

function formatTs(ts: string | null): string {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('tr-TR');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AdminArcelikScraper = () => {
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const [results, setResults] = useState<ProductRecord[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [wasRunning, setWasRunning] = useState(false);

  // Image scraper (dedicated high-quality gallery scraper)
  const [imageStatus, setImageStatus] = useState<ImageServiceStatus | null>(null);
  const [imageStarting, setImageStarting] = useState(false);
  const [imageStopping, setImageStopping] = useState(false);
  const [imageSyncing, setImageSyncing] = useState(false);

  // -------------------------------------------------------------------------
  // Auth guard
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!adminLoading && !isPlatformAdmin) {
      toast({ title: 'Erişim Reddedildi', description: 'Bu sayfaya erişim yetkiniz bulunmamaktadır.', variant: 'destructive' });
      navigate('/');
    }
  }, [isPlatformAdmin, adminLoading, navigate, toast]);

  // -------------------------------------------------------------------------
  // Fetch service status
  // -------------------------------------------------------------------------
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${SCRAPER_SERVICE_URL}/status`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ServiceStatus = await res.json();
      setStatus(data);
      setServiceError(null);
    } catch (err: any) {
      setServiceError(
        err.message === 'Failed to fetch'
          ? 'Scraper servisi çalışmıyor. Lütfen başlatın: cd arcelik-scraper && source .venv/bin/activate && python src/main.py'
          : `Servis hatası: ${err.message}`,
      );
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll every 2 seconds
  useEffect(() => {
    if (!isPlatformAdmin) return;
    fetchStatus();
    const id = setInterval(fetchStatus, 2000);
    return () => clearInterval(id);
  }, [isPlatformAdmin, fetchStatus]);

  // -------------------------------------------------------------------------
  // Image scraper status polling
  // -------------------------------------------------------------------------
  const fetchImageStatus = useCallback(async () => {
    try {
      const res = await fetch(`${SCRAPER_SERVICE_URL}/image-status`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ImageServiceStatus = await res.json();
      setImageStatus(data);
    } catch {
      setImageStatus(null); // service down — handled by the shared serviceError banner
    }
  }, []);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    fetchImageStatus();
    const id = setInterval(fetchImageStatus, 2000);
    return () => clearInterval(id);
  }, [isPlatformAdmin, fetchImageStatus]);

  // -------------------------------------------------------------------------
  // Auto-load results when scraping finishes
  // -------------------------------------------------------------------------
  useEffect(() => {
    const currentlyRunning = status?.state?.running ?? false;
    if (wasRunning && !currentlyRunning) {
      fetchResults(selectedCategory);
    }
    setWasRunning(currentlyRunning);
  }, [status?.state?.running]);

  // -------------------------------------------------------------------------
  // Fetch results from DB
  // -------------------------------------------------------------------------
  const fetchResults = useCallback(async (category?: string | null) => {
    setResultsLoading(true);
    try {
      const cat = category ?? selectedCategory;
      const url = cat
        ? `${SCRAPER_SERVICE_URL}/results?limit=200&category=${encodeURIComponent(cat)}`
        : `${SCRAPER_SERVICE_URL}/results?limit=200`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(data.data || []);
    } catch {
      // silently fail — DB results are secondary
    } finally {
      setResultsLoading(false);
    }
  }, [selectedCategory]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------
  const handleStartScrape = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${SCRAPER_SERVICE_URL}/scrape`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast({ title: 'Kazıma Başlatıldı', description: data.message });
      } else {
        throw new Error(data.detail || data.message || 'Bilinmeyen hata');
      }
    } catch (err: any) {
      toast({ title: 'Başlatma Hatası', description: err.message, variant: 'destructive' });
    } finally {
      setStarting(false);
      fetchStatus();
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      const res = await fetch(`${SCRAPER_SERVICE_URL}/stop`, { method: 'POST' });
      const data = await res.json();
      toast({ title: 'Durdurma Sinyali Gönderildi', description: data.message });
    } catch (err: any) {
      toast({ title: 'Hata', description: err.message, variant: 'destructive' });
    } finally {
      setStopping(false);
      fetchStatus();
    }
  };

  // ── Image scraper actions ──────────────────────────────────────────────
  const handleStartImageScrape = async () => {
    setImageStarting(true);
    try {
      const res = await fetch(`${SCRAPER_SERVICE_URL}/scrape-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast({ title: 'Görsel Kazıma Başlatıldı', description: data.message });
      } else {
        throw new Error(data.detail || data.message || 'Bilinmeyen hata');
      }
    } catch (err: any) {
      toast({ title: 'Başlatma Hatası', description: err.message, variant: 'destructive' });
    } finally {
      setImageStarting(false);
      fetchImageStatus();
    }
  };

  const handleStopImageScrape = async () => {
    setImageStopping(true);
    try {
      const res = await fetch(`${SCRAPER_SERVICE_URL}/stop-images`, { method: 'POST' });
      const data = await res.json();
      toast({ title: 'Durdurma Sinyali Gönderildi', description: data.message });
    } catch (err: any) {
      toast({ title: 'Hata', description: err.message, variant: 'destructive' });
    } finally {
      setImageStopping(false);
      fetchImageStatus();
    }
  };

  // Trigger the image-sync edge function (pushes the HQ gallery to the store).
  const handleImageSync = async () => {
    setImageSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('image-sync', { body: {} });
      if (error) throw error;
      toast({
        title: 'Görsel Senkronizasyonu Başlatıldı',
        description: `Mağazaya yüksek kaliteli görseller gönderiliyor (log #${data?.logId ?? '—'}).`,
      });
    } catch (err: any) {
      toast({ title: 'Senkronizasyon Hatası', description: err.message, variant: 'destructive' });
    } finally {
      setImageSyncing(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------
  if (adminLoading || (!isPlatformAdmin && !adminLoading)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            {adminLoading ? (
              <><RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" /><p>Yetki kontrol ediliyor...</p></>
            ) : (
              <><AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-4" /><p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p></>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  const scraperState = status?.state;
  const isRunning = scraperState?.running ?? false;

  const imageState = imageStatus?.state;
  const imageRunning = imageState?.running ?? false;
  const imageProgressPct = imageState && imageState.total_products > 0
    ? Math.round((imageState.processed / imageState.total_products) * 100)
    : 0;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Layout>
      <div className="p-6 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Globe className="w-8 h-8 text-blue-500" />
              Arçelik Genel Katalog Kazıyıcı
            </h1>
            <p className="text-muted-foreground">
              arcelik.com.tr genel ürün kataloğundan fiyat ve ürün verisi kazıma (Scrapling)
            </p>
          </div>
          <Button onClick={fetchStatus} variant="outline" size="icon">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* ── Service error banner ── */}
        {serviceError && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Servis Bağlantı Hatası</p>
                  <p className="text-sm text-muted-foreground mt-1">{serviceError}</p>
                  <code className="block mt-2 text-xs bg-muted p-2 rounded">
                    cd arcelik-scraper &amp;&amp; source .venv/bin/activate &amp;&amp; pip install -r requirements.txt &amp;&amp; python -m playwright install chromium &amp;&amp; python src/main.py
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Quick stats ── */}
        {status && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-blue-600">{status.today_record_count ?? '—'}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  <Database className="w-3 h-3" /> Bugünkü Kayıt
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold">{scraperState?.total_products_found ?? 0}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  <Package className="w-3 h-3" /> Bulunan Ürün
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-green-600">{scraperState?.total_inserted ?? 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Yeni Eklenen</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-orange-600">{scraperState?.total_updated ?? 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Güncellenen</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Main control card ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Start / Stop */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                Kazımayı Başlat / Durdur
              </CardTitle>
              <CardDescription>
                Tüm {status?.categories.length ?? 12} kategoriden otomatik olarak ürün verisi çekilir.
                Giriş gerekmez — arcelik.com.tr herkese açık bir sitedir.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isRunning ? (
                <Button
                  onClick={handleStartScrape}
                  disabled={starting || serviceError !== null}
                  className="w-full h-16 text-lg"
                  size="lg"
                >
                  {starting ? (
                    <><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Başlatılıyor...</>
                  ) : (
                    <><Play className="w-5 h-5 mr-2" />Kazımayı Başlat</>
                  )}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                    <div>
                      <p className="font-medium text-blue-700 dark:text-blue-300">Kazıma devam ediyor…</p>
                      {scraperState?.current_category && (
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Şu an: {scraperState.current_category}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Progress bar */}
                  {status && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{scraperState?.categories_scraped ?? 0} / {status.categories.length} kategori</span>
                        <span>{Math.round(((scraperState?.categories_scraped ?? 0) / status.categories.length) * 100)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-700"
                          style={{ width: `${((scraperState?.categories_scraped ?? 0) / status.categories.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleStop}
                    disabled={stopping}
                    variant="destructive"
                    className="w-full"
                  >
                    {stopping ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Durduruluyor...</>
                    ) : (
                      <><Square className="w-4 h-4 mr-2" />Durdur</>
                    )}
                  </Button>
                </div>
              )}

              {scraperState?.started_at && !isRunning && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Son kazıma: {formatTs(scraperState.finished_at || scraperState.started_at)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category list */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                Kazınacak Kategoriler
              </CardTitle>
              <CardDescription>
                {status?.categories.length ?? 12} adet arcelik.com.tr kategorisi
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-56">
                <ul className="space-y-1 text-sm">
                  {(status?.categories ?? []).map((cat) => {
                    const isCurrent = scraperState?.current_category === cat.name;
                    const isDone = (scraperState?.categories_scraped ?? 0) > cat.index;
                    return (
                      <li
                        key={cat.index}
                        className={`flex items-center gap-2 px-2 py-1 rounded ${isCurrent ? 'bg-blue-50 dark:bg-blue-950' : ''}`}
                      >
                        {isCurrent ? (
                          <RefreshCw className="w-3 h-3 text-blue-500 animate-spin flex-shrink-0" />
                        ) : isDone ? (
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        ) : (
                          <div className="w-3 h-3 rounded-full border border-muted-foreground flex-shrink-0" />
                        )}
                        <span className={isCurrent ? 'font-medium text-blue-700 dark:text-blue-300' : isDone ? 'text-muted-foreground line-through' : ''}>
                          {cat.name}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* ── Image scraper (dedicated high-quality gallery) ── */}
        <Card className="border-purple-200 dark:border-purple-900">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Images className="w-6 h-6 text-purple-500" />
              Görsel Kazıyıcı & Mağaza Senkronizasyonu
            </CardTitle>
            <CardDescription>
              Her ürünün detay sayfasındaki <strong>tüm galeri görsellerini</strong> en yüksek
              kalitede (2000×2000) indirir ve depoya yükler. Ardından "Mağazaya Gönder" ile
              yüksek kaliteli görseller e-ticaret sitesine aktarılır.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Image scraper stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-bold">{imageState?.total_products ?? 0}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Hedef Ürün</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-bold text-blue-600">{imageState?.processed ?? 0}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">İşlenen</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-bold text-green-600">{imageState?.products_with_images ?? 0}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Görselli Ürün</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-bold text-purple-600">{imageState?.total_images_mirrored ?? 0}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">İndirilen Görsel</div>
              </div>
            </div>

            {/* Progress while running */}
            {imageRunning && imageState && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {imageState.processed} / {imageState.total_products} ürün
                    {imageState.current_product ? ` — şu an: ${imageState.current_product}` : ''}
                  </span>
                  <span>{imageProgressPct}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all duration-700"
                    style={{ width: `${imageProgressPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {!imageRunning ? (
                <Button
                  onClick={handleStartImageScrape}
                  disabled={imageStarting || serviceError !== null}
                  className="h-14 bg-purple-600 hover:bg-purple-700"
                >
                  {imageStarting ? (
                    <><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Başlatılıyor...</>
                  ) : (
                    <><Images className="w-5 h-5 mr-2" />Görselleri Kaz (Tüm Envanter)</>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleStopImageScrape}
                  disabled={imageStopping}
                  variant="destructive"
                  className="h-14"
                >
                  {imageStopping ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Durduruluyor...</>
                  ) : (
                    <><Square className="w-4 h-4 mr-2" />Görsel Kazımayı Durdur</>
                  )}
                </Button>
              )}

              <Button
                onClick={handleImageSync}
                disabled={imageSyncing}
                variant="outline"
                className="h-14 border-purple-300"
              >
                {imageSyncing ? (
                  <><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Gönderiliyor...</>
                ) : (
                  <><UploadCloud className="w-5 h-5 mr-2" />Mağazaya Gönder (image-sync)</>
                )}
              </Button>
            </div>

            {imageState?.finished_at && !imageRunning && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Son görsel kazıma: {formatTs(imageState.finished_at || imageState.started_at)}
              </div>
            )}

            {/* Image scraper logs */}
            {imageState && imageState.logs.length > 0 && (
              <ScrollArea className="h-48 w-full rounded border bg-muted/50 p-3 font-mono text-xs">
                {[...imageState.logs].reverse().map((log, i) => (
                  <div
                    key={i}
                    className={`py-0.5 ${
                      log.level === 'error' ? 'text-destructive' :
                      log.level === 'warn'  ? 'text-yellow-600 dark:text-yellow-400' :
                      log.level === 'success' ? 'text-green-600 dark:text-green-400' :
                      'text-muted-foreground'
                    }`}
                  >
                    <span className="text-muted-foreground/60">
                      [{new Date(log.timestamp).toLocaleTimeString('tr-TR')}]
                    </span>
                    {' '}{log.message}
                  </div>
                ))}
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* ── Status row ── */}
        {status && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Servis Durumu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {serviceError ? (
                    <><XCircle className="w-5 h-5 text-destructive" /><span className="text-destructive">Bağlanamıyor</span></>
                  ) : isRunning ? (
                    <><RefreshCw className="w-5 h-5 text-blue-500 animate-spin" /><span className="text-blue-600 font-medium">Çalışıyor</span></>
                  ) : (
                    <><CheckCircle className="w-5 h-5 text-green-600" /><span>Hazır</span></>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Veritabanı</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant={status.supabase_configured ? 'default' : 'destructive'}>
                  {status.supabase_configured ? 'Yapılandırıldı' : 'Yapılandırılmadı'}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Hatalar</CardTitle>
              </CardHeader>
              <CardContent>
                {scraperState?.errors.length ? (
                  <Badge variant="destructive">{scraperState.errors.length} hata</Badge>
                ) : (
                  <Badge variant="secondary">Hata yok</Badge>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Logs ── */}
        {status && scraperState && scraperState.logs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="w-5 h-5" /> Loglar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64 w-full rounded border bg-muted/50 p-4 font-mono text-xs">
                {[...scraperState.logs].reverse().map((log, i) => (
                  <div
                    key={i}
                    className={`py-0.5 ${
                      log.level === 'error' ? 'text-destructive' :
                      log.level === 'warn'  ? 'text-yellow-600 dark:text-yellow-400' :
                      log.level === 'success' ? 'text-green-600 dark:text-green-400' :
                      'text-muted-foreground'
                    }`}
                  >
                    <span className="text-muted-foreground/60">
                      [{new Date(log.timestamp).toLocaleTimeString('tr-TR')}]
                    </span>
                    {' '}
                    <span className="font-medium uppercase">[{log.level}]</span>
                    {' '}{log.message}
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* ── Results Table ── */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" /> Bugünkü Ürünler (DB)
              </CardTitle>
              <div className="flex items-center gap-2">
                {status && (
                  <select
                    className="text-sm border rounded px-2 py-1 bg-background"
                    value={selectedCategory ?? ''}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      setSelectedCategory(val);
                      fetchResults(val);
                    }}
                  >
                    <option value="">Tüm Kategoriler</option>
                    {status.categories.map((c) => (
                      <option key={c.index} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchResults(selectedCategory)}
                  disabled={resultsLoading || serviceError !== null}
                >
                  {resultsLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <CardDescription>
              Bugün veritabanına kaydedilmiş ürünler. Kazıma bitince otomatik yüklenir.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {results.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Henüz sonuç yok. Kazımayı başlatın veya "Yenile" butonuna tıklayın.</p>
              </div>
            ) : (
              <ScrollArea className="h-[32rem]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs">
                      <th className="text-left pb-2 pr-3 w-16">Resim</th>
                      <th className="text-left pb-2 pr-3">Ürün Adı</th>
                      <th className="text-left pb-2 pr-3">Ürün Kodu</th>
                      <th className="text-left pb-2 pr-3">Kategori</th>
                      <th className="text-right pb-2 pr-3">Liste Fiyatı</th>
                      <th className="text-right pb-2">İndirimli Fiyat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => {
                      const hasDiscount = r.discounted_price != null && r.price != null && r.discounted_price < r.price;
                      const discountPct = hasDiscount
                        ? Math.round((1 - r.discounted_price! / r.price!) * 100)
                        : 0;
                      return (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                          <td className="py-2 pr-3">
                            {r.image_url ? (
                              <img
                                src={r.image_url}
                                alt={r.product_name}
                                className="w-12 h-12 object-contain rounded border"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-muted rounded border flex items-center justify-center">
                                <Image className="w-4 h-4 text-muted-foreground" />
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3 max-w-xs">
                            <div className="font-medium leading-tight truncate">{r.product_name}</div>
                            {r.sub_category && (
                              <div className="text-[10px] text-muted-foreground truncate mt-0.5">{r.sub_category}</div>
                            )}
                            {r.product_url && (
                              <a
                                href={r.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline flex items-center gap-0.5 mt-0.5"
                              >
                                arcelik.com.tr <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <div className="font-mono text-xs text-muted-foreground">{r.product_code ?? '-'}</div>
                            <div className="text-[10px] text-muted-foreground/70 mt-0.5">{r.sku ?? ''}</div>
                          </td>
                          <td className="py-2 pr-3">
                            {r.category ? (
                              <Badge variant="outline" className="text-xs">{r.category}</Badge>
                            ) : '-'}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <span className={hasDiscount ? 'text-muted-foreground line-through text-xs' : 'font-medium'}>
                              {formatTurkishPrice(r.price)}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            {hasDiscount ? (
                              <span className="flex flex-col items-end gap-0.5">
                                <span className="font-medium text-green-700 dark:text-green-400">
                                  {formatTurkishPrice(r.discounted_price)}
                                </span>
                                <Badge variant="secondary" className="text-[10px] text-green-700 bg-green-50 dark:bg-green-950">
                                  <TrendingDown className="w-3 h-3 mr-0.5" />
                                  %{discountPct}
                                </Badge>
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* ── Instructions ── */}
        <Card>
          <CardHeader>
            <CardTitle>Kullanım Talimatları</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium">1. Python ortamını hazırlayın</h4>
              <code className="block bg-muted p-3 rounded text-sm whitespace-pre">
{`cd arcelik-scraper
python -m venv .venv
.venv\\Scripts\\activate        # Windows
pip install -r requirements.txt
playwright install chromium    # headless tarayıcıyı indir`}
              </code>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">2. .env dosyasını oluşturun</h4>
              <code className="block bg-muted p-3 rounded text-sm whitespace-pre">
{`cp .env.example .env
# .env içinde SUPABASE_SERVICE_ROLE_KEY değerini girin`}
              </code>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">3. Servisi başlatın</h4>
              <code className="block bg-muted p-3 rounded text-sm">
                python src/main.py
              </code>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">4. Veritabanı tablosunu oluşturun</h4>
              <p className="text-sm text-muted-foreground">
                Supabase SQL editöründe <code className="bg-muted px-1 rounded">arcelik_public_products.sql</code> dosyasını çalıştırın.
              </p>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">Mars Kazıyıcı ile fark</h4>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li><strong>Bu servis (port 7078):</strong> arcelik.com.tr — herkese açık katalog, giriş gerektirmez, Scrapling kullanır.</li>
                <li><strong>Mars servisi (port 7077):</strong> mars.arcelik.com.tr — bayi portali, manuel 2FA girişi gerektirir, Playwright kullanır.</li>
                <li>İki servis tamamen birbirinden bağımsızdır.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

      </div>
    </Layout>
  );
};

export default AdminArcelikScraper;
